/**
 * Which game mechanics actually exist in the live Base Sepolia diamond today.
 *
 * Source of truth: the article-alignment audit and docs/CONTRACT_ROADMAP.md.
 * Anything not listed as live is designed but NOT on-chain yet, so the UI must
 * label it "coming" and must not present it as an active event.
 */

/** Chaos event types that can really occur on-chain right now. */
export const LIVE_CHAOS_EVENTS = new Set<string>([
  'ghost_key',        // PHANTOM: 1-10% chance a buy does not extend the timer
  'blood_moon',       // SURGE: every 10th round
  'surge_protocol',   // THALIA naming for blood_moon
  'groundhog_day',    // <5 keys: round resets (max 3)
  'cursed_purchase',  // Winner's Curse
  'singularity_shadow',
  'assassin_mode',    // TERMINAL: exists, but only when switched on by the operator
  // Phase 2.2 / 2.5 chaos, enabled on-chain Nov 2026 (chaosConfig + chaosEventsConfig mask 31)
  'extinction_event', 'flash_crash', 'fomo_surge', 'volcanic_jackpot',
  'charity_chaos', 'chaos_carnival', 'thalia_carnival', 'dividend_storm', 'golden_round', 'lottery_round',
]);

/** Chaos event types that are designed but not on-chain yet. */
export const COMING_CHAOS_EVENTS = new Set<string>([
  'whale_alert', 'redistribution', 'resistance_aid',
]);

export const isComingChaosEvent = (type: string) => COMING_CHAOS_EVENTS.has(type);

/** Contract rejects buys when now >= deadline - 15s (LibAppStorage.TIMESTAMP_BUFFER). */
export const BUY_LOCK_SECONDS = 15;

/** Extinction timer drops are live on-chain (ChaosEventsFacet). */
export const EXTINCTION_LIVE = true;

export const PIONEER_PAYOUT_LIVE = false;
export const FACTION_JACKPOT_PAYOUT_LIVE = false;

/** Phase 2.1 on-chain Seasons (SeasonFacet). Off unless VITE_SEASONS_ENABLED=true (preview first). */
export const SEASONS_LIVE = import.meta.env.VITE_SEASONS_ENABLED !== 'false';

/** Phase 2.2 chaos batch 1 (ChaosFacet: Charity + Carnival). Off unless VITE_CHAOS_ENABLED=true (preview first). Shows 'coming' until the facet is cut and enabled. */
export const CHAOS_PANEL_ENABLED = import.meta.env.VITE_CHAOS_ENABLED !== 'false';

/** Phase 2.3 faction powers batch 1 (FactionFacet: Raven/Ape/Rabbit/Wolf bonus keys). Off unless VITE_FACTIONS_ENABLED=true (preview first). Shows 'coming' until the facet is cut and enabled. */
export const FACTIONS_PANEL_ENABLED = import.meta.env.VITE_FACTIONS_ENABLED !== 'false';

/** Phase 2.4 pooled faction powers (FactionPoolFacet: Viper lottery, Rabbit top 3, Ape stampede, Wolf rescue, Moth first wave, Fox underdogs, Owl night watch). Ships disabled; every pooled power shows 'coming' unless this flag is on. */
export const FACTION_POOLS_LIVE = import.meta.env.VITE_FACTION_POOLS_ENABLED !== 'false';

/** Phase 2.5 chaos events batch 2 (ChaosEventsFacet: Flash Crash, FOMO Surge, Volcanic, Extinction, Golden/Lottery/Storm rounds). Ships disabled; the event types stay in COMING_CHAOS_EVENTS until the owner enables them. */
export const CHAOS_EVENTS_2_LIVE = import.meta.env.VITE_CHAOS_EVENTS_2_ENABLED !== 'false';

/** Phase 2.6 unclaimed test FRAME decay (DecayFacet: 20% after 72h idle, 100% after 7 days, to active key holders). Ships disabled; 'coming' until enabled. */
export const DECAY_LIVE = import.meta.env.VITE_DECAY_ENABLED !== 'false';
/** Phase 2.7 extras (ExtrasFacet: Kingmaker, Activity timer, Bounties). Ship OFF (extrasConfig mask 0): every item shows 'coming'. */
export const EXTRAS_LIVE = import.meta.env.VITE_EXTRAS_ENABLED === 'true';
